export const selectFeatureFlagsLoaded = (state) => Boolean(state.FeatureFlags && state.FeatureFlags.loaded);

// Cosmetic developer indicator (panel visibility). The backend still enforces developerCheck
// on every write, so this only shows/hides UI.
export const selectIsDeveloper = (state) => Boolean(state.FeatureFlags && state.FeatureFlags.developer);

export const selectFeatureFlagsMap = (state) => (state.FeatureFlags && state.FeatureFlags.flags) || {};

/**
 * Resolve a single flag to a boolean.
 *
 * Until the flags have loaded, or if the code isn't in the resolved map, return `fallback`.
 * Choose the fallback by intent:
 *   - MODULE gating (bookings/cameras/…): fallback = true — grandfather-safe, never hide a
 *     paid/available module because a flag is still loading or unknown.
 *   - NEW-feature gating (cloud-key-ui, the Developer panel): fallback = false — don't flash
 *     a feature that should stay hidden until the flag is known ON.
 */
// Pure resolver from an already-held flags map + loaded flag. Reused by selectFeatureEnabled
// and by composed module selectors (e.g. selectActiveModules) that carry the map as a
// memoized createSelector input rather than the whole state.
export const resolveFeatureFromMap = (flags, loaded, code, fallback = false) => {
	if (!loaded) return fallback;
	return Object.prototype.hasOwnProperty.call(flags || {}, code) ? Boolean(flags[code]) : fallback;
};

export const selectFeatureEnabled = (state, code, fallback = false) => {
	const ff = state.FeatureFlags;
	return resolveFeatureFromMap(ff && ff.flags, ff && ff.loaded, code, fallback);
};

// ── Developer-panel registry selectors ──
const selectAdmin = (state) => (state.FeatureFlags && state.FeatureFlags.admin) || {};
export const selectRegistryFlags = (state) => selectAdmin(state).flags || [];
export const selectRegistryOverrides = (state) => selectAdmin(state).overrides || [];
export const selectRegistryLoading = (state) => Boolean(selectAdmin(state).loading);
export const selectRegistryLoaded = (state) => Boolean(selectAdmin(state).loaded);
export const selectRegistryMutating = (state) => Boolean(selectAdmin(state).mutating);
export const selectRegistryError = (state) => selectAdmin(state).error || null;

// Org lookup (override modal): all orgs, and their loading state.
export const selectOrgs = (state) => selectAdmin(state).orgs || [];
export const selectOrgsLoading = (state) => Boolean(selectAdmin(state).orgsLoading);
